import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  Avatar,
  Typography,
  Divider,
  InputAdornment,
  Rating
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircle';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  successfulLogin,
  LoginWithSquareSpaceBtn,
  emailRegex,
  paths,
  squareSpaceUtm,
  isLoginOrSignup,
  helmetTitles,
  squareSpaceUtmAPIExt,
  squareSpaceUtmOnBoardingAPIExt,
  loginPageTexts,
  hostNames,
  localSeoLogo,
  localseoSignup,
  localSeoSignupSource,
  squareSpaceUtmLocalSeo,
  localseoSignupSqspFlow,
  localSeoSignupSourceSqsp,
  SqSpIconPath,
  SEOSpaceLocal
} from 'src/common/config/TextMessage';
import { API } from 'src/common/config/API';
import {
  getCookie,
  localStorageMethod,
  removeSquareSpaceToken,
  setCookie,
  preventSpaceInput
} from 'src/common/config/FunctionHelper';
import { ILoginForm } from 'src/content/login/types/formTypes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { setIsPremiumUser, setTempId } from 'src/redux/userReducer';
import { loginGTMTrackEvent } from 'src/common/Analytics';
import CommonAlert from 'src/common/CommonAlert';
import {
  handleSnackBar,
  hasLocalSeoCookie,
  removeUtmCookie,
  setLocalSeoCookie,
  setUtmCookie
} from 'src/common/config/FunctionHelperPartTwo';
import { Helmet } from 'react-helmet-async';
import { loginSxs } from './loginSxs';
import EyeIcon from 'src/components/Icons/EyeIcon';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { AvatarGroupModule } from 'src/common/AvatarGroupsModule';
import { signupSxs } from 'src/content/signup/components/signupSx';
import { LocalSeoIconSx } from 'src/common/CommomSxs';
import { LocalSeoToolPage } from 'src/content/signup/components/localSeoSignupComponenets/LocalSeoToolPage';
import { setIsOpenChangePlan } from 'src/redux/siteAuditReducer';

/**
 * Login component.
 * Renders the login form.
 * @component
 * @returns {JSX.Element}
 */
const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginSqSPLoader, setLoginSqSPLoader] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ILoginForm>();
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] =
    React.useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hostname = window.location.hostname;
  const isProduction = hostname.includes(hostNames?.prod);
  let planDetails = useSelector((state: RootState) => state.user.planDetails);
  // get getCookie
  let utm = getCookie(squareSpaceUtm);
  const UtmOnBoardingLocal = localStorageMethod('get', 'is_utm_onboarding');
  const getLocalSeoCookie = getCookie(localseoSignup) ?? null;
  const getLocalSeoCookieSqsp = getCookie(localseoSignupSqspFlow) ?? null;
  const isLocalSeo = getLocalSeoCookie === localSeoSignupSource;
  const isLocalSeoSqsp = getLocalSeoCookieSqsp === localSeoSignupSourceSqsp;
  const isFromLoginSqsp =
    localStorageMethod('get', 'from_login_sqsp') == 'true';
  const isLocalSeoSession = hasLocalSeoCookie();

  useEffect(() => {
    if (isFromLoginSqsp) {
      localStorageMethod('remove', 'from_login_sqsp');
    }
    localStorageMethod('remove', 'temp_id');
    dispatch(setTempId(null));
  }, []);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Login Form Submit handler.
   * Handles form submission and user login.
   * @param {ILoginForm} request - The login form data.
   */
  const onSubmit: SubmitHandler<ILoginForm> = async (request) => {
    removeUtmCookie(isLoginOrSignup);
    setLoading(true);
    setError('');
    const { email, password } = request;
    API.login(
      API.EndPoint.login,
      API.Method.post,
      undefined,
      { email, password },
      (response) => {
        try {
          const responseDetails = { ...response?.data };
          if (responseDetails?.status === 'success') {
            setLoading(false);
            setIsSuccessfullySubmitted(true);
            setCookie(responseDetails?.token, responseDetails?.user?.name);
            const planName =
              responseDetails.user?.subscription?.plan_name?.toLowerCase();
            const isFreeWithAI = responseDetails?.isFreeWithAI;
            dispatch(setIsPremiumUser(planName));
            localStorageMethod('remove', 'is_from_utm_signup');
            setTimeout(() => {
              loginGTMTrackEvent(false);
              if (planName !== planDetails?.free) {
                if (utm || isLocalSeoSqsp) {
                  squareSpaceAuth();
                } else {
                  navigate(paths?.dashboard);
                }
              } else {
                if (utm) {
                  squareSpaceAuth();
                } else if (isLocalSeoSqsp) {
                  dispatch(setIsOpenChangePlan(true));
                  navigate(paths?.billing);
                } else {
                  navigate(
                    isFreeWithAI ? paths?.dashboard : paths?.externalScans
                  );
                }
              }
            }, 1500);
          } else {
            setError(responseDetails?.message);
            setTimeout(() => {
              setError('');
              setLoading(false);
            }, 4000);
          }
        } catch (error) {
          if (error?.response?.status == 429) {
            handleSnackBar();
          }
        }
      }
    );
  };

  // Redirect to the squareSpaceAuth screen
  const squareSpaceAuth = () => {
    const endPointExtension =
      isLocalSeoSqsp && !utm
        ? squareSpaceUtmLocalSeo
        : UtmOnBoardingLocal == 'true'
        ? squareSpaceUtmOnBoardingAPIExt
        : squareSpaceUtmAPIExt;
    removeSquareSpaceToken();
    API.squareAuth(
      // API.EndPoint.squareAuth + '?utm=squarespace_market',
      API.EndPoint.squareAuth + endPointExtension,
      API.Method.get,
      undefined,
      {},
      (response) => {
        location.href = response?.data?.authUrl;
      }
    );
  };

  // Login validation validation
  const validationErrorMessageLogin: any = {
    email: {
      required: 'Email Address is required',
      pattern: {
        value: emailRegex,
        message: 'Invalid email address'
      }
    },
    password: {
      required: 'Password is required'
    }
  };

  /**
   * Handles the Square Space page navigation.
   * Redirects to the Square Space authentication URL.
   */
  const handelClickSquareSpaceJoin = () => {
    setLoginSqSPLoader(true);
    removeSquareSpaceToken();
    localStorageMethod('set', 'from_login_sqsp', 'true');
    localStorageMethod('remove', 'is_from_utm_signup');
    setUtmCookie(isLoginOrSignup, 'true');
    const endPointExtension = '';
    //const endPointExtension =
    //  (isLocalSeoSession || isLocalSeo) && !utm
    //    ? squareSpaceNonUtmLocalSeo
    //    : '';
    if (isLocalSeoSession) {
      setLocalSeoCookie();
    }
    API.squareAuth(
      API.EndPoint.squareAuth + endPointExtension,
      API.Method.get,
      undefined,
      {},
      (response) => {
        location.href = response?.data?.authUrl;
        setTimeout(() => {
          setLoginSqSPLoader(false);
        }, 2000);
      }
    );
  };

  return (
    <>
      <Helmet>
        <title>
          {isLocalSeo ? `${SEOSpaceLocal} | Login` : helmetTitles?.loginHelmet}
        </title>
        {isProduction && (
          <>
            <meta
              name="description"
              content="Log in to SEOSpace and supercharge your SEO workflow."
            />
            <meta name="robots" content="index, follow" />
            <meta name="googlebot" content="index, follow" />
          </>
        )}
        {/* Additional meta tags as needed for SEO and social media */}
      </Helmet>
      <Box sx={loginSxs?.mainBoxSx}>
        {/* Left Box for Image */}
        {(isLocalSeo || isLocalSeoSession) && !utm ? (
          <Box sx={loginSxs?.leftBoxSx}>
            <LocalSeoToolPage />
          </Box>
        ) : (
          <Box sx={loginSxs?.leftBoxSx}>
            <Box sx={loginSxs?.avatarGroupSx}>
              <Box>
                <AvatarGroupModule />
              </Box>
              <Box sx={loginSxs?.ratingBoxSx}>
                <Box sx={loginSxs?.ratingSx}>
                  <Rating
                    sx={loginSxs?.rattingSx}
                    name="size-large"
                    defaultValue={5}
                    readOnly
                  />
                  <span
                    style={{
                      ...loginSxs?.ratingScoreSx,
                      color: 'var(--color-white)'
                    }}
                  >
                    4.9
                  </span>
                </Box>
                <Typography
                  sx={{
                    ...loginSxs?.reviewSx,
                    ...loginSxs?.rattingsTextColorSx
                  }}
                >
                  From 100+ reviews
                </Typography>
              </Box>
            </Box>

            <img
              src={process.env.PUBLIC_URL + '/static/images/login/login2.png'}
              alt="img"
              style={{
                ...loginSxs?.imgSx,
                objectFit: 'contain',
                paddingTop: '4vh'
              }}
            />
          </Box>
        )}
        {/* Right Box for Text */}
        <Box sx={loginSxs?.rightBoxSx}>
          <Box sx={loginSxs?.subRightBoxSx}>
            <img
              src={
                (isLocalSeo || isLocalSeoSession) && !utm
                  ? process.env.PUBLIC_URL + localSeoLogo
                  : process.env.PUBLIC_URL + '/static/images/logo/new-logo.svg'
              }
              alt="img"
              style={
                (isLocalSeo || isLocalSeoSession) && !utm
                  ? { ...loginSxs?.logoSx, ...LocalSeoIconSx }
                  : loginSxs?.logoSx
              }
            />
            <Typography sx={loginSxs?.welcomeBackTextSx}>
              {loginPageTexts?.welcomeBack}
            </Typography>
            <Typography sx={loginSxs?.descSx}>
              {loginPageTexts?.desc}
            </Typography>
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit(onSubmit)}
              sx={loginSxs?.loginFieldBoxSx}
            >
              <Stack spacing={2} height="100%" alignItems="center">
                {/* Email Input */}
                <TextField
                  id="outlined-email-input"
                  variant="outlined"
                  //label="Email"
                  placeholder="Email"
                  size="small"
                  type="text"
                  autoComplete="email"
                  fullWidth
                  //InputProps={{ disableUnderline: true }}
                  {...preventSpaceInput(
                    register('email', validationErrorMessageLogin.email)
                  )}
                  helperText={errors.email ? errors.email.message : ''}
                  error={!!errors.email}
                  sx={{ ...loginSxs?.textFieldSx }}
                  InputProps={{
                    sx: {
                      ...loginSxs?.inputProps
                    }
                  }}
                  InputLabelProps={{
                    sx: {
                      ...loginSxs?.inputLabelProps
                    }
                  }}
                />
                {/* Password Input */}
                <TextField
                  id="outlined-password-input"
                  variant="outlined"
                  //label="Password"
                  placeholder="Password"
                  size="small"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  fullWidth
                  {...preventSpaceInput(
                    register('password', validationErrorMessageLogin.password)
                  )}
                  helperText={errors.password ? errors.password.message : ''}
                  error={!!errors.password}
                  sx={{ ...loginSxs?.textFieldSx }}
                  InputProps={{
                    sx: {
                      ...loginSxs?.inputProps
                    },
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={loginSxs.passwordEyeSx}
                        onClick={handleTogglePasswordVisibility}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon
                            sx={signupSxs?.eyeOffIcon}
                          />
                        ) : (
                          <EyeIcon />
                        )}
                      </InputAdornment>
                    )
                  }}
                  InputLabelProps={{
                    sx: {
                      ...loginSxs?.inputLabelProps
                    }
                  }}
                />
                <Typography
                  onClick={() => {
                    navigate(paths?.forgot);
                  }}
                  sx={loginSxs?.forgotTextSx}
                >
                  {loginPageTexts?.forgot}
                </Typography>
                {/* Success and Error Messages */}
                <div className="flex w-full">
                  {isSuccessfullySubmitted && (
                    <div className="mt-2 w-full">
                      {' '}
                      <Alert
                        icon={
                          <CheckCircleOutlineIcon
                            fontSize="inherit"
                            sx={loginSxs?.loginStatusSx}
                          />
                        }
                        severity="success"
                      >
                        {successfulLogin}
                      </Alert>
                    </div>
                  )}
                  {error && (
                    <div className="mt-2 w-full">
                      <CommonAlert severity="error" message={error} />
                    </div>
                  )}
                </div>
                <Stack width="100%">
                  <Box className="flex-col items-center justify-center">
                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      sx={loginSxs.loginButtonSx}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Login'}
                    </Button>
                    <Divider sx={loginSxs?.dividerSx}>
                      <span style={loginSxs?.dividerTextSx}>or</span>
                    </Divider>
                  </Box>
                </Stack>
                <Stack width="100%">
                  <Box className="flex-col items-center justify-center">
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        handelClickSquareSpaceJoin();
                      }}
                    >
                      {loginSqSPLoader ? (
                        <CircularProgress size={24} />
                      ) : (
                        <>
                          <Avatar sx={loginSxs?.avatarSx} src={SqSpIconPath} />

                          <Typography sx={loginSxs?.sqspBtnTextSx}>
                            {LoginWithSquareSpaceBtn}
                          </Typography>
                        </>
                      )}
                    </Button>
                  </Box>
                  <Box sx={loginSxs?.signUpTextBoxSx}>
                    <Typography
                      onClick={() => {
                        //navigate(paths?.signup);
                      }}
                      sx={loginSxs?.haveNotAccountTextSx}
                    >
                      {loginPageTexts?.notAccount}
                    </Typography>
                    <Typography
                      onClick={() => {
                        navigate(
                          isLocalSeoSqsp && !utm
                            ? paths?.signupLocalSeoExt
                            : (isLocalSeo || isLocalSeoSession) && !utm
                            ? paths?.signupLocalSeo
                            : paths?.signup
                        );
                      }}
                      sx={loginSxs?.signUpTextSx}
                    >
                      {loginPageTexts?.signUp}
                    </Typography>
                  </Box>
                  <Box className="mt-4" sx={loginSxs?.avatarResponsiveSx}>
                    <Box>
                      <AvatarGroupModule />
                    </Box>
                    <Box sx={loginSxs?.ratingBoxSx}>
                      <Box sx={loginSxs?.ratingSx}>
                        <Rating
                          sx={loginSxs?.rattingSx}
                          name="size-large"
                          defaultValue={5}
                          readOnly
                        />
                        <span
                          style={{
                            ...loginSxs?.ratingScoreSx,
                            ...loginSxs?.loginRattingSx
                          }}
                        >
                          4.9
                        </span>
                      </Box>
                      <Typography
                        sx={{
                          ...loginSxs?.reviewSx,
                          ...loginSxs?.reviewerTextColor
                        }}
                      >
                        From 100+ reviews
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Login;
